import { useEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { GridScan } from './GridScan';

gsap.registerPlugin(ScrollTrigger);

/* ================= CONFIG ================= */

const TOTAL_FRAMES_DESKTOP = 1435;
const TOTAL_FRAMES_MOBILE = 1460;
const VIDEO_FPS = 30;
const SCROLL_MULTIPLIER = 16;
const OUTRO_SCROLL = 200;
const IDLE_FRAMES = 80;
const IDLE_FPS = 30;

/* -------- CONTENT -------- */
const CONTENT = [
  {
    id: '1',
    from: 0,
    to: 120,
    title: 'Leading the Future of Immersive ',
    highlight: 'Spatial Intelligence',
    position:
      'left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2',
    customStyle: 'items-center justify-center text-center px-4',
    headingStyle: 'max-w-3xl text-3xl md:text-5xl',
  },
  {
    id: '2',
    from: 260,
    to: 380,
    title: 'Train Safer. Operate Smarter. ',
    highlight: 'Scale Faster.',
    position:
      'left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2',
    customStyle: 'items-center justify-center text-center px-4',
    headingStyle: 'max-w-3xl text-3xl md:text-5xl',
  },
];

/* ================= COMPONENT ================= */

export default function VideoCanvasSequenceHero() {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const videoRef = useRef(null);

  const durationRef = useRef(0);
  const currentFrameRef = useRef(0);
  const idleRafRef = useRef(null);
  const scrollTriggerRef = useRef(null);

  const contentRefs = useRef({});

  const [loaded, setLoaded] = useState(false);
  const [frame, setFrame] = useState(0);
  const [isOutro, setIsOutro] = useState(false);

  const isMobile = useMemo(() => {
    return (
      window.matchMedia('(max-width: 768px)').matches ||
      /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    );
  }, []);

  const TOTAL_FRAMES = isMobile
    ? TOTAL_FRAMES_MOBILE
    : TOTAL_FRAMES_DESKTOP;

  const VIDEO_SRC = isMobile
    ? '/updated_seq/mobile/output.mp4'
    : '/updated_seq/web_new/output.mp4';

  /* ================= CANVAS SETUP ================= */

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      canvas.width = vw * dpr;
      canvas.height = vh * dpr;
      canvas.style.width = `${vw}px`;
      canvas.style.height = `${vh}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawFrame();
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  /* ================= VIDEO LOAD ================= */

  useEffect(() => {
    setLoaded(false);

    const video = document.createElement('video');
    video.src = VIDEO_SRC;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';

    videoRef.current = video;

    video.onloadedmetadata = () => {
      durationRef.current = video.duration;

      // 🔥 PRIME DECODER (NO AUTOPLAY)
      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        setLoaded(true);
        drawFrame();
      };

      video.addEventListener('seeked', onSeeked, { once: true });
      video.currentTime = 0.001;
    };

    return () => {
      video.pause();
      videoRef.current = null;
    };
  }, [isMobile]);

  /* ================= DRAW FRAME ================= */

  const drawFrame = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const video = videoRef.current;

    if (!canvas || !ctx || !video || video.readyState < 2) return;

    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;

    ctx.clearRect(0, 0, cw, ch);

    const vr = video.videoWidth / video.videoHeight;
    const cr = cw / ch;

    let dw, dh, dx = 0, dy = 0;

    if (vr > cr) {
      dh = ch;
      dw = dh * vr;
      dx = (cw - dw) / 2;
    } else {
      dw = cw;
      dh = dw / vr;
      dy = (ch - dh) / 2;
    }

    ctx.drawImage(video, dx, dy, dw, dh);
  };

  /* ================= IDLE ANIMATION ================= */

  useEffect(() => {
    if (!loaded) return;

    let idleFrame = 0;
    let dir = 1;
    let last = 0;

    const animate = (t) => {
      if (t - last > 1000 / IDLE_FPS) {
        idleFrame += dir;

        if (idleFrame >= IDLE_FRAMES - 1 || idleFrame <= 0) {
          dir *= -1;
        }

        seekToFrame(idleFrame);
        last = t;
      }

      idleRafRef.current = requestAnimationFrame(animate);
    };

    idleRafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(idleRafRef.current);
      idleRafRef.current = null;
    };
  }, [loaded]);

  /* ================= SEEK ================= */

  const seekToFrame = (f) => {
    const video = videoRef.current;
    if (!video || !durationRef.current) return;

    const t = Math.min(
      Math.max((f / (TOTAL_FRAMES - 1)) * durationRef.current, 0.001),
      durationRef.current - 0.04
    );

    if (Math.abs(video.currentTime - t) > 0.001) {
      video.currentTime = t;
    }

    if ('requestVideoFrameCallback' in video) {
      video.requestVideoFrameCallback(drawFrame);
    } else {
      requestAnimationFrame(drawFrame);
    }

    currentFrameRef.current = f;
    setFrame(f);
  };

  /* ================= SCROLL ================= */

  useEffect(() => {
    if (!loaded) return;

    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top top',
      end: `+=${TOTAL_FRAMES * SCROLL_MULTIPLIER + OUTRO_SCROLL}`,
      scrub: true,
      pin: true,
      anticipatePin: 1,

      onUpdate: (self) => {
        const total = TOTAL_FRAMES * SCROLL_MULTIPLIER;
        const pos = self.progress * (total + OUTRO_SCROLL);

        if (pos >= total) {
          setIsOutro(true);
          seekToFrame(TOTAL_FRAMES - 1);
          return;
        }

        setIsOutro(false);

        const f = Math.round((pos / total) * (TOTAL_FRAMES - 1));

        if (f > 1 && idleRafRef.current) {
          cancelAnimationFrame(idleRafRef.current);
          idleRafRef.current = null;
        }

        if (f !== currentFrameRef.current) {
          seekToFrame(f);
        }
      },
    });

    return () => scrollTriggerRef.current?.kill();
  }, [loaded]);

  /* ================= TEXT ================= */

  useEffect(() => {
    CONTENT.forEach((b) => {
      const el = contentRefs.current[b.id];
      if (!el) return;

      gsap.to(el, {
        autoAlpha: frame >= b.from && frame <= b.to ? 1 : 0,
        duration: 0.3,
        ease: 'power2.out',
      });
    });
  }, [frame]);

  /* ================= RENDER ================= */

  return (
    <>
      {!loaded && (
        <div className="fixed inset-0 z-[9999] bg-black">
          <GridScan />
          <p className="absolute inset-0 flex items-center justify-center text-white">
            Loading…
          </p>
        </div>
      )}

      <section
        ref={sectionRef}
        className="relative w-full h-screen overflow-hidden bg-black"
      >
        <canvas
          ref={canvasRef}
          className="absolute  w-full h-full"
        />

        <div className="pointer-events-none absolute inset-0 z-10">
          {CONTENT.map((b) => (
            <div
              key={b.id}
              ref={(el) => (contentRefs.current[b.id] = el)}
              className={`absolute flex flex-col opacity-0 ${b.customStyle} ${b.position}`}
            >
              <h1 className={`text-white font-bold ${b.headingStyle}`}>
                {b.title}
                <span className="text-primary"> {b.highlight}</span>
              </h1>
            </div>
          ))}
        </div>

        {isOutro && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black">
            <h1 className="text-white text-4xl md:text-5xl text-center">
              A Boundless Frontier for
              <span className="text-primary"> Spatial Intelligence</span>
            </h1>
          </div>
        )}

        <div className="absolute bottom-4 left-4 text-white/50 text-sm">
          Frame: {frame}/{TOTAL_FRAMES}
        </div>
      </section>
    </>
  );
}
